// CAPTURE — type the way you think ("ραντεβού με Άννα αύριο 10:30", "call George Friday 3pm") and
// AVRASTRO shows, live and in place, exactly what it understood: the words it recognised are highlighted
// in the sentence itself, and the result is a row of chips. Enter creates it. Nothing is guessed
// silently — an ambiguous name is offered as choices (Tab cycles).
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { createTask, addNote } from '@vero/domain';
import { parseCapture, type CaptureResult } from '@vero/intelligence';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Avatar } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { TaskForm, CaseForm } from './forms.tsx';

export function kindLabel(rt: Runtime, kind: CaptureResult['kind']): string {
  if (kind === 'note') return rt.t('capture.kind_note');
  if (kind === 'case') return rt.t('capture.kind_case');
  return rt.i18n.enumLabel('taskKind', kind);
}

/** Turn a parse into a real record. Appointments without a time and applications open their form prefilled. */
export async function commitCapture(rt: Runtime, r: CaptureResult, personId: string | undefined, text: string): Promise<boolean> {
  const { today } = rt;
  const person = personId ? rt.view.get('persons', personId) : undefined;
  const title = r.title || (person ? `${kindLabel(rt, r.kind)} · ${person.displayName}` : kindLabel(rt, r.kind));
  if (r.kind === 'case') { rt.open(<CaseForm personId={personId ?? null} />); return true; }
  if (r.kind === 'appointment' && (!r.date || !r.time)) {
    rt.open(<TaskForm personId={personId ?? null} kind="appointment" preset={{ title, dueDate: r.date ?? today, dueTime: r.time ?? null }} />);
    return true;
  }
  const kindText = kindLabel(rt, r.kind);
  const cs = r.kind === 'note'
    ? await rt.run((v, c) => addNote(v, { personId: personId ?? null, body: r.title || text.trim() }, c), 'capture.created', { kind: kindText })
    : await rt.run((v, c) => createTask(v, {
      kind: r.kind === 'call' ? 'call' : r.kind === 'appointment' ? 'appointment' : r.kind === 'task' ? 'task' : 'followup',
      title, personId: personId ?? null, dueDate: r.date ?? today, dueTime: r.time ?? null, durationMin: r.durationMin ?? null, origin: 'capture',
    }, c), 'capture.created', { kind: kindText });
  return !!cs;
}

export function CaptureBox({ onDone, autoFocus, compact }: { onDone?: () => void; autoFocus?: boolean; compact?: boolean }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const [text, setText] = useState('');
  const [pick, setPick] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const people = useMemo(() => view.rows('persons').filter((p) => !p.archivedAt && p.stage !== 'dependent')
    .map((p) => ({ id: p.id, displayName: p.displayName, firstName: p.firstName, lastName: p.lastName })), [view]);
  const r = useMemo(() => (text.trim() ? parseCapture(text, { today, people }) : null), [text, today, people]);
  const personId = r ? (pick >= 0 ? r.candidates[pick]?.id : r.personId) : undefined;
  const person = personId ? view.get('persons', personId) : undefined;
  const needsTime = r?.kind === 'appointment' && (!r.date || !r.time);

  const create = async () => {
    if (!r) return;
    if (await commitCapture(rt, r, personId, text)) { setText(''); setPick(-1); onDone?.(); }
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && r) { e.preventDefault(); e.stopPropagation(); void create(); }
    if (e.key === 'Tab' && r && r.candidates.length > 1 && !r.personId) { e.preventDefault(); setPick((p) => (p + 1) % r.candidates.length); }
  };
  const onScroll = () => { if (mirrorRef.current && inputRef.current) mirrorRef.current.scrollLeft = inputRef.current.scrollLeft; };

  return (
    <div className={`capture${compact ? ' compact' : ''}`} data-testid="capture">
      <div className="capture-inner">
        <div className="capture-row">
          <Icon name="spark" size={18} />
          <div className="capture-field">
            <div className="mirror" ref={mirrorRef} aria-hidden="true">{highlight(text, r)}</div>
            <input ref={inputRef} autoFocus={autoFocus} value={text} placeholder={t('capture.placeholder')} aria-label={t('capture.title')} data-testid="capture-input"
              onChange={(e: { target: HTMLInputElement }) => { setText(e.target.value); setPick(-1); requestAnimationFrame(onScroll); }} onKeyDown={onKey} onScroll={onScroll} />
          </div>
          {r && <button className="btn btn-primary btn-sm" onClick={() => void create()} data-testid="capture-create">{t('capture.create', { kind: kindLabel(rt, r.kind) })}<span className="kbd" style={{ background: 'transparent', borderColor: 'currentColor', color: 'inherit' }}>↵</span></button>}
        </div>
        {r && (
          <div className="capture-parse" data-testid="capture-parse" aria-live="polite">
            <span className="lead">{t('capture.understood')}</span>
            <span className="pchip kind" data-testid="capture-kind">{kindLabel(rt, r.kind)}</span>
            {(r.date || r.time) && <span className="pchip when" data-testid="capture-when"><Icon name="agenda" size={13} />{[r.date ? i18n.date(r.date, 'weekday') : '', r.time ? i18n.time(r.time) : '', r.durationMin ? `${r.durationMin}′` : ''].filter(Boolean).join(' · ')}</span>}
            {person && <span className="pchip who" data-testid="capture-who"><Avatar name={person.displayName} size="xs" />{person.displayName}</span>}
            {!person && r.candidates.length > 1 && <>
              <span className="muted small">{t('capture.choose_person')}</span>
              {r.candidates.slice(0, 4).map((c, i) => (
                <button key={c.id} type="button" className="pchip cand" aria-selected={pick === i} onMouseDown={(e: MouseEvent) => { e.preventDefault(); setPick(i); inputRef.current?.focus(); }}><Avatar name={c.name} size="xs" />{c.name}</button>
              ))}
            </>}
            {!person && r.candidates.length <= 1 && r.kind !== 'note' && <span className="pchip warn">{t('capture.no_person')}</span>}
            {r.title && <span className="pchip" style={{ maxWidth: 280 }}><span className="one-line" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>“{r.title}”</span></span>}
            {needsTime && <span className="pchip warn">{t('capture.needs_time')}</span>}
          </div>
        )}
        {!compact && <div className="capture-foot"><span>{t('capture.hint')}</span><span>el · en</span></div>}
      </div>
    </div>
  );
}

function highlight(text: string, r: CaptureResult | null): ReactNode[] {
  if (!r || !r.spans.length) return [text];
  const spans = [...r.spans].sort((a, b) => a.start - b.start);
  const out: ReactNode[] = [];
  let at = 0;
  spans.forEach((s, i) => {
    if (s.start < at) return;
    if (s.start > at) out.push(text.slice(at, s.start));
    out.push(<mark key={i} className={s.type}>{text.slice(s.start, s.end)}</mark>);
    at = s.end;
  });
  out.push(text.slice(at));
  return out;
}
